import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment";
import React, { useState, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { FiEdit } from "react-icons/fi";
import * as Yup from "yup";
import { Trash } from "../../constant";
import { daysOfWeek } from "../../data";
import AreYouSureModal from "../../modals/AreYouSureModal";
import { Input } from "../Input";
import LeftSide from "../LeftSide";
import MultiSelectDropdown from "../MultiSelect";
import StartAndEndTime from "../StartAndEndTime";
import classes from "./ManageSlotForm.module.css";
import { useEffect } from "react";
import Popover from "../Popovers";

const initialValues = {
  courtStartTime: null,
  courtEndTime: null,
  courtCharges: "",
  courtDayOfWeek: [],
  active: true,
};
const getDayName = (dayId) => {
  return daysOfWeek.find(item => item.value === dayId)?.label
}


const validationSchema = Yup.object().shape({
  courtStartTime: Yup.string().required("Start time is required"),
  courtEndTime: Yup.string()
    .required("End time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (endTime) {
        const { courtStartTime } = this.parent;
        // If either time is missing, skip validation
        if (!courtStartTime || !endTime) return true;
        // Parse the time strings
        const startTime = moment(moment(courtStartTime), "HH:mm:ss");
        const endTimeMoment = moment(moment(endTime), "HH:mm:ss");
        // If parsing failed, skip validation
        if (!startTime.isValid() || !endTimeMoment.isValid()) return true;
        // Compare the times
        return endTimeMoment.isAfter(startTime);
      }
    ),
  courtCharges: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(0, "Amount cannot be negative"),
  courtDayOfWeek: Yup.array()
    .of(Yup.number().required())
    .min(1, "At least one day of week must be selected"),
});

const ManageSlotForm = ({ initialData, onDelete, slot, onSaveCourtTime, courtId }) => {
  const [edit, setEdit] = useState(false);
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [deleteIndx, setDeleteIndx] = useState(null);
  const [showDayDeleteModal, setShowDayDeleteModal] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const menuRef = useRef(null);

  const formik = useFormik({
    initialValues: initialData || initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      if (typeof onSaveCourtTime === "function") {
        const params = {
          ...values, 
          courtId: courtId?courtId:null,
          courtStartTime: moment(values.courtStartTime).set('seconds', 0).format("HH:mm:ss"),
          courtEndTime: moment(values.courtEndTime).set('seconds', 0).format("HH:mm:ss"),
        }
        onSaveCourtTime(params);
        if(!courtId ){
          setEdit(true); // Set edit to true after successful submission
        }
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    resetForm,
    setFieldError,
    isValid,
    dirty,
  } = formik;
  // Map day IDs to day names from daysOfWeek array
  const handleChangeWithTouch = (field) => (value) => {
    setFieldValue(field, value);
    setFieldTouched(field, false, false);
  };
  const handleTimeChange = (timeType, value) => {
    const field = timeType === "start" ? "courtStartTime" : "courtEndTime";
    handleChangeWithTouch(field)(value);
  };
  // Handle form submission with explicit event capturing
  const handleFormSubmitWithoutRefresh = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Stop event bubbling
    } // Call Formik's submit handler
    handleSubmit(e);
    // Return false to ensure no default behavior
    return false;
  };

  // Handle button click instead of form submit
  // const handleButtonClick = (index, e) => {
  //   setSlotIndx(index);
  //   e.preventDefault();
  //   e.stopPropagation();
  //   handleSubmit(index);
  // };

  useEffect(() => {
    if (slot) {
      console.log("SLOT",slot)
      // For new slots (without courtTimingId), keep edit mode false
      if (!slot.courtTimingId) {
        setEdit(false);
        return;
      }

      setEdit(true);
      const validSlotsData = (slot?.slotsData || []).filter(item => item !== null);
      if(courtId){
      setFieldValue(
        "courtDayOfWeek",
        validSlotsData.map((item) => item?.courtDayOfWeek)
      );}else{
        setFieldValue(
          "courtDayOfWeek",
         values.courtDayOfWeek
        );
      }
      setFieldValue("courtStartTime", moment(slot?.courtStartTime, "HH:mm:ss"));
      setFieldValue("courtEndTime", moment(slot?.courtEndTime, "HH:mm:ss"));
      setFieldValue("courtCharges", slot?.courtCharges);
    }
  }, [slot]);

  // Add a new effect to handle edit state when slot data changes
  useEffect(() => {
    if (slot?.courtTimingId) {
      setEdit(true);
    }
  }, [slot?.courtTimingId]);

  const handleDelete = (selectedData) => {
    console.log("Jelly Selected Data", selectedData);
    if (typeof onDelete === "function") {
      onDelete(selectedData);
    }
  };

  const getDayName = (dayId) => {
    const day = daysOfWeek.find(d => d.value === dayId);
    return day ? day.label : '';
  };

  const getMenuItems = () => {
    const items = [];
    
    if (slot?.slotsData && slot?.slotsData?.length > 0) {
      slot?.slotsData?.forEach((data, index) => {
        items.push({
          label: getDayName(data?.courtDayOfWeek),
          command: () => handleDelete(data)
        });
      });

      if (slot.slotsData.length > 1) {
        items.push({
          label: 'All',
          command: () => handleDelete(slot?.slotsData)
        });
      }
    }

    return items;
  };

  return (
    // Use a div wrapper instead of form to avoid nesting forms
    <>
      <div className={classes.form}>
        <Row className={classes.slotRow}>
          <Col xs={12} md={3}>
            <MultiSelectDropdown
              disabled={edit}
              display="comma"
              options={daysOfWeek}
              placeholder="Select Days"
              selectionMode="multiple"
              value={values.courtDayOfWeek || []}
              onChange={(selectedValues) => {
                setFieldValue("courtDayOfWeek", selectedValues);
                setFieldTouched("courtDayOfWeek", true, false);
                if (selectedValues && selectedValues.length > 0) {
                  setFieldError("courtDayOfWeek", undefined);
                }
              }}
              errorText={touched.courtDayOfWeek && errors.courtDayOfWeek}
            />
          </Col>
          <Col xs={12} md={5}>
            <div className={classes.timePickerContainer}>
              <StartAndEndTime
                disabled={edit}
                startTime={values.courtStartTime}
                endTime={values.courtEndTime}
                onChange={handleTimeChange}
                errors={{
                  startTime: touched.courtStartTime && errors.courtStartTime,
                  endTime: touched.courtEndTime && errors.courtEndTime,
                }}
              />
            </div>
          </Col>
          <Col xs={12} md={2}>
            <Input
              disabled={edit}
              type="number"
              placeholder="Enter charges"
              value={values.courtCharges}
              onChange={handleChangeWithTouch("courtCharges")}
              onBlur={handleBlur}
              leftIcon={<LeftSide right="Rs." />}
              customStyle={{ gap: "15px" }}
              inputStyle={{ paddingLeft: "0" }}
              errorText={touched.courtCharges && errors.courtCharges}
            />
          </Col>
          <Col xs={12} md={2} className={classes.actionColumn}>
            <div className={classes.actionWrapper}>
              {edit && !slot?.courtTimingId && (
                <div className={classes.actionButtons}>
                  <button
                    className={clsx(classes.iconButton, classes.editButton)}
                    type="button"
                    onClick={() => {
                      setEdit(false);
                    }}
                  >
                    <FiEdit />
                  </button>
                </div>
              )}

              {!edit && (
                <div className={classes.actionButtons}>
                  <button
                    type="button"
                    className={classes.button}
                    onClick={handleFormSubmitWithoutRefresh}
                  >
                    Save
                  </button>
                </div>
              )}
              {slot?.slotsData && slot.slotsData.length > 1 ? (
                <Popover items={getMenuItems()}>
                  <button
                    type="button"
                    className={classes.deleteButton}
                  >
                    <Trash />
                  </button>
                </Popover>
              ) : (
                <div className={classes.actionButtons}>
                  <button
                    type="button"
                    className={classes.deleteButton}
                    onClick={() => handleDelete(slot

                    )}
                  >
                    <Trash />
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {showAreYouSureModal && (
        <AreYouSureModal
          title="Slot"
          show={showAreYouSureModal}
          setShow={setShowAreYouSureModal}
          onConfirm={() => {
            onDelete(deleteIndx);
          }}
        />
      )}
    </>
  );
};

export default ManageSlotForm;
